// mpErrorHumano — Traduce códigos de error de MercadoPago a mensajes user-friendly.
//
// Contexto: MP devuelve códigos técnicos (cc_rejected_*, CC_VAL_*, card_disabled)
// que no significan nada para un usuario final. Este helper convierte esos códigos
// en frases claras en español rioplatense para mostrar en pantalla.
//
// Referencias:
//   https://www.mercadopago.com.uy/developers/es/docs/checkout-api/response-handling/collection-results
//   Códigos observados en prod Limpack: CC_VAL_433 (caso Marcela 17-jul, test Nico 18-jul).

const CODIGOS = {
  // Datos inválidos ingresados por el user
  cc_rejected_bad_filled_security_code: 'El código de seguridad (CVV) no es correcto. Fijate en el reverso de la tarjeta.',
  cc_rejected_bad_filled_date: 'La fecha de vencimiento no es correcta.',
  cc_rejected_bad_filled_card_number: 'El número de tarjeta no es correcto. Revisalo y probá de nuevo.',
  cc_rejected_bad_filled_other: 'Alguno de los datos de la tarjeta no es correcto. Revisalos y probá de nuevo.',
  CC_VAL_433: 'Los datos de la tarjeta no son válidos. Revisá número, vencimiento y CVV.',

  // Fondos / límites
  cc_rejected_insufficient_amount: 'Tu tarjeta no tiene fondos suficientes para esta operación.',
  insufficient_amount: 'Tu tarjeta no tiene fondos suficientes para esta operación.',

  // Banco pide validación
  cc_rejected_call_for_authorize: 'Tu banco pide autorización para hacer este pago. Llamalos y después volvé a intentar.',
  cc_rejected_high_risk: 'Tu banco rechazó la operación por seguridad. Probá con otra tarjeta o llamalos.',

  // Tarjeta bloqueada / no habilitada
  cc_rejected_card_disabled: 'Tu tarjeta está deshabilitada. Llamá al banco emisor.',
  card_disabled: 'Tu tarjeta está deshabilitada. Llamá al banco emisor.',
  cc_rejected_card_type_not_allowed: 'Este tipo de tarjeta no está habilitado para pagos recurrentes.',

  // Límites de intentos y duplicados
  cc_rejected_max_attempts: 'Superaste el límite de intentos. Esperá unos minutos o probá con otra tarjeta.',
  cc_rejected_duplicated_payment: 'Detectamos una operación duplicada. Esperá un momento y probá de nuevo.',

  // Otros
  cc_rejected_invalid_installments: 'La cantidad de cuotas no es válida.',
  cc_rejected_other_reason: 'Tu banco rechazó la operación. Probá con otra tarjeta o consultá con tu banco.',
}

/**
 * Devuelve un mensaje humano para mostrar al user, a partir de un código de error MP.
 * Si no hay código o no lo conocemos, devuelve un mensaje genérico útil.
 *
 * @param {string|null} code   Código MP (ej. 'cc_rejected_bad_filled_security_code', 'CC_VAL_433')
 * @param {string|null} fallback  Mensaje custom para usar si no hay match (opcional)
 * @returns {string}
 */
export function mpErrorHumano(code, fallback) {
  if (!code) return fallback || 'No pudimos completar el pago. Revisá los datos de la tarjeta y probá de nuevo. Si el problema sigue, escribinos por WhatsApp.'
  const clean = String(code).trim()
  if (CODIGOS[clean]) return CODIGOS[clean]
  return fallback || `No pudimos completar el pago (${clean}). Revisá los datos de la tarjeta o probá con otra. Si el problema sigue, escribinos por WhatsApp.`
}

/**
 * Extrae el código de error MP desde el body de respuesta del edge function o del FunctionsHttpError.
 * MP devuelve la causa en distintas ubicaciones según el tipo de error:
 *   - detalle.cause[0].code       → preapproval con error de validación
 *   - detalle.status_detail        → payment rechazado
 *   - detalle.error / detalle.message → errores genéricos
 *   - error (top-level)            → error de nuestro edge
 *
 * @param {object|null} body   Body parseado de la respuesta (ver crearPreapprovalCliente)
 * @returns {string|null}
 */
export function extraerCodigoMP(body) {
  if (!body) return null
  const d = body.detalle || body
  return (
    (Array.isArray(d?.cause) && d.cause[0]?.code) ||
    d?.status_detail ||
    d?.code ||
    d?.error ||
    body?.code ||
    null
  )
}
